<script lang="ts">
  import HoverMenu from "$components/editor/extensions/blocks/HoverMenu/HoverMenu.svelte";
  import GutterMenu from "../../+shared/GutterMenu.svelte";
  import {
    Alignment,
    CSS_ALIGNMENT,
    Size,
  } from "$components/editor/extensions/blocks/shared/attributes";

  import { NodeViewWrapper } from "svelte-tiptap";
  import { onMount } from "svelte";
  import { getMediaQuery } from "app/utils/mediaQuery";
  import type { Editor, NodeViewProps } from "@tiptap/core";

  export let editor: Editor;
  export let node: NodeViewProps["node"];
  export let updateAttributes: NodeViewProps["updateAttributes"];
  export let deleteNode: NodeViewProps["deleteNode"];
  export let selected: NodeViewProps["selected"];
  export let isEditable: boolean;

  $$restProps; // prevent svelte unused props warning

  let gifWidth = 0;

  const isTabletAndDesktop = getMediaQuery("tablet");
  $: ({ gif, width, align } = node.attrs);

  // prevent broken gifs (from copy/paste or otherwise) crashing editor
  $: if (gif && !gif.images) {
    deleteNode();
  }

  const CSS_Size = {
    SMALL: !$isTabletAndDesktop ? 1 / 2 : 1 / 4, // 50% mobile, 25% desktop
    MEDIUM: !$isTabletAndDesktop ? 1 : 1 / 3, // 100% mobile, 33% desktop
    LARGE: 1, // 100% mobile, 100% desktop
    ORIGINAL: 1, // 100% mobile, 100% desktop
  };

  $: cssWidth = CSS_ALIGNMENT[align as Alignment];

  // reactively set GIF width based on editor size (to prevent overflow)
  onMount(() => {
    const editorElem = editor.options.element;
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries && entries.length) {
        // set width as a proportion of the editor size, defaulting to medium
        gifWidth = editorElem.clientWidth * (CSS_Size[width as Size] ?? CSS_Size.SMALL);
      }
    });
    if (editorElem) resizeObserver.observe(editorElem);
    return () => editorElem && resizeObserver.unobserve(editorElem);
  });

  const changeSize = (width: Size) => {
    updateAttributes({
      width,
    });
    gifWidth = editor.options.element.clientWidth * (CSS_Size[width as Size] ?? CSS_Size.SMALL);
  };

  const setAlign = (align: Alignment) => {
    updateAttributes({
      align,
    });
  };
</script>

<NodeViewWrapper class="not-prose group relative">
  {#if isEditable}
    <GutterMenu on:delete-block={deleteNode} />
  {/if}
  <div class={`flex flex-row rounded ${cssWidth}`} contentEditable={false}>
    <div class="relative w-fit rounded" class:ring-2={selected && isEditable}>
      <!-- { only show when we know how big it should be } -->
      {#if gif && gif.images && gifWidth}
        <div class="bg-gray-200 p-4 text-center text-gray-500">
          GIF functionality is not available
        </div>
      {/if}
      {#if isEditable}
        <div
          class="absolute inset-x-0 bottom-0 z-[999] mb-2 hidden translate-y-full justify-center group-hover:flex"
        >
          <HoverMenu
            {align}
            {width}
            on:sizeChange={(e) => changeSize(e.detail)}
            on:alignChange={(e) => setAlign(e.detail)}
          />
        </div>
      {/if}
    </div>
  </div>
</NodeViewWrapper>
